import { computed, markRaw, toValue } from "vue";
import { useI18n } from "vue-i18n";
import { useStore } from "vuex";

import { format, isBefore, isToday, isYesterday, startOfDay } from "date-fns";

import { CommunicationType } from "@/enums/communicationLogs";
import { isSmsStatusFailed } from "@/enums/sms";
import {
  isAiAgentLog,
  isCallLog,
  isEmailLog,
  isNoteLog,
  isSmsLog,
  isTaskLog,
} from "@/helpers/communication";
import { formatUserFullName } from "@/helpers/formatting";
import { useEmailLogInfo } from "@/hooks/communicationLogs";

import IconAINav from "@/components/icons/IconAINav.vue";
import IconMessage from "@/components/icons/IconMessage.vue";
import IconTask from "@/components/icons/IconTask.vue";
import IconTelephone from "@/components/icons/IconTelephone.vue";
import IconDealNotes from "@/components/icons/dealNotes/DealNotes.vue";

import type {
  AiAgentSessionLog,
  CallLog,
  CallLogData,
  CommunicationLogBase,
  CommunicationUser,
  EmailLinksClicked,
  EmailLog,
  NoteLog,
  SmsLog,
  TaskLog,
} from "@/models/communicationLogs";
import type { IUser } from "@/models/users";
import type { MaybeRef } from "vue";

const RawIconAINav = markRaw(IconAINav);
const RawIconTelephone = markRaw(IconTelephone);
const RawIconDealNotes = markRaw(IconDealNotes);
const RawIconMessage = markRaw(IconMessage);
const RawIconTask = markRaw(IconTask);

export interface ThreadDataConfig {
  call?: {
    backgroundClass: string | ((log: CallLog) => string);
    extras?: (log: CallLog) => Record<string, unknown>;
  };
  sms?: {
    backgroundClass: string;
    extras?: (log: SmsLog) => Record<string, unknown>;
  };
  note?: {
    extras?: (log: NoteLog) => Record<string, unknown>;
  };
  ai_agent?: Record<string, never>;
}

export const useCommunicationThreadData = (
  logSource: MaybeRef<CommunicationLogBase>,
  config?: ThreadDataConfig
) => {
  const { getters } = useStore();
  const { t } = useI18n();

  const user = computed<IUser | null>(() => getters["auth/user"]);

  const getChattableNames = (data: {
    sender: CommunicationUser;
    receiver: CommunicationUser;
    thread_messages_count?: number;
  }) => {
    if (!data) {
      return "";
    }
    const { sender, receiver, thread_messages_count } = data;

    if (thread_messages_count === 1) {
      return sender.id &&
        user.value &&
        sender.id?.toString() === user.value.id?.toString()
        ? t("COMMON.ME")
        : (sender.name || "").split(" ")[0];
    }

    const senderName = (sender.name || "").split(" ")[0];
    const receiverName = (receiver.name || "").split(" ")[0];

    return [senderName, receiverName].filter(Boolean).join(", ");
  };

  const getCallNames = (data: CallLogData) => {
    const { callee, user } = data;

    return [callee?.name?.split(" ")[0], user?.first_name]
      .filter(Boolean)
      .join(", ");
  };

  const emailLogData = (log: EmailLog) => {
    const { iconInfo } = useEmailLogInfo(log.data);

    return {
      icon: iconInfo.value,
      namesAndCount: {
        names: getChattableNames(log.data),
        count: log.data.thread_messages_count || 0,
        linksClicked: log.data.links_clicked as unknown as
          | EmailLinksClicked[]
          | undefined,
      },
      message: [log.data.subject, log.data.content_text]
        .filter(Boolean)
        .join(" - "),
      id: log.data.id,
      dataCy: "email-thread",
    };
  };

  const callLogData = (log: CallLog) => {
    const bgClass = config?.call?.backgroundClass;
    const backgroundClass =
      typeof bgClass === "function"
        ? bgClass(log)
        : (bgClass ?? "bg-woods-green");

    return {
      icon: {
        icon: RawIconTelephone,
        backgroundClass,
        viewBox: "0 0 16 16",
        isOutgoing: !!log.data?.is_outgoing,
        type: CommunicationType.call,
        ...config?.call?.extras?.(log),
      },
      namesAndCount: {
        names: getCallNames(log.data),
        count: 0,
      },
      message: log.data?.body || t("COMMUNICATION.NO_CALL_NOTE"),
      id: log.id,
      dataCy: "call-thread",
    };
  };

  const noteLogData = (log: NoteLog) => {
    return {
      icon: {
        icon: RawIconDealNotes,
        backgroundClass: "bg-flush-orange",
        viewBox: "0 0 16 16",
        ...config?.note?.extras?.(log),
      },
      namesAndCount: {
        names: log.data?.user?.full_name || "",
        count: 0,
      },
      message: log.data?.comment,
      id: log.id,
      dataCy: "note-thread",
    };
  };

  const smsLogData = (log: SmsLog) => {
    const failed = isSmsStatusFailed(log.data.status);

    return {
      icon: {
        icon: RawIconMessage,
        backgroundClass: failed
          ? "bg-error"
          : (config?.sms?.backgroundClass ?? "bg-turquoise"),
        viewBox: "0 0 16 16",
        isOutgoing: !!log.data?.is_outgoing,
        ...config?.sms?.extras?.(log),
      },
      namesAndCount: {
        names: getChattableNames(log.data),
        count: log.data?.conversation_messages_count || 0,
      },
      message: log.data?.message,
      id: log.data?.id,
      dataCy: "sms-thread",
    };
  };

  const getTaskDueDateDisplay = (dueAt: string | undefined) => {
    if (!dueAt) {
      return { label: "", textClass: "" };
    }

    const dueDate = new Date(dueAt);
    const isOverdue = isBefore(startOfDay(dueDate), startOfDay(new Date()));
    const textClass = isOverdue ? "text-error" : "";

    if (isToday(dueDate)) {
      return { label: t("ACTIVITY.TASK.DUE_TODAY"), textClass };
    }

    if (isYesterday(dueDate)) {
      return { label: t("ACTIVITY.TASK.DUE_YESTERDAY"), textClass };
    }

    return {
      label: `${t("ACTIVITY.TASK.DUE")} ${format(dueDate, "MMM d, yyyy")}`,
      textClass,
    };
  };

  const taskLogData = (log: TaskLog) => {
    const dueDateDisplay = getTaskDueDateDisplay(log.data?.due_at);

    return {
      icon: {
        icon: RawIconTask,
        backgroundClass: "bg-secondary-purple-1",
        viewBox: "0 0 12 12",
      },
      namesAndCount: {
        names: dueDateDisplay.label,
        count: 0,
        textClass: dueDateDisplay.textClass,
      },
      message: log.data?.body,
      id: log.id,
      dataCy: "task-thread",
    };
  };

  const getAiAgentNames = (log: AiAgentSessionLog) => {
    const agentName = log.data?.agent?.name || "";
    const userName = log.data?.user ? formatUserFullName(log.data.user) : "";
    return [agentName, userName].filter(Boolean).join(", ");
  };

  const aiAgentLogData = (log: AiAgentSessionLog) => {
    return {
      icon: {
        icon: RawIconAINav,
        backgroundClass: "bg-secondary-purple",
        viewBox: "0 0 24 24",
        class: "text-white",
      },
      namesAndCount: {
        names: getAiAgentNames(log),
        count: 0,
      },
      message: log.data?.first_message?.message || "",
      id: log.id,
      dataCy: "ai-agent-thread",
    };
  };

  const activeThreadData = computed(() => {
    const log = toValue(logSource);

    if (isEmailLog(log)) {
      return emailLogData(log);
    }
    if (isCallLog(log)) {
      return callLogData(log);
    }
    if (isNoteLog(log)) {
      return noteLogData(log);
    }
    if (isTaskLog(log)) {
      return taskLogData(log);
    }
    if (isSmsLog(log)) {
      return smsLogData(log);
    }
    if (isAiAgentLog(log)) {
      return aiAgentLogData(log);
    }

    return null;
  });

  return { activeThreadData };
};
